<!--
  预览画布组件
  职责：根据工程分辨率设置，渲染等比缩放的预览画布
  数据来源：workspace store 的 project.screenHash
-->
<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useMessage } from 'naive-ui'
import CanvasEditOverlay from '@/components/workspace/CanvasEditOverlay.vue'
import PreviewSurface from '@/components/workspace/PreviewSurface.vue'
import WebGLPreviewRenderer from '@/components/workspace/WebGLPreviewRenderer.vue'
import { getCachedImageResource } from '@/features/preview/imageResourceCache'
import {
  startPreviewTestEngineInFrame,
  stopPreviewTestEngineInFrame,
  notifyPreviewTestEngineResize,
} from '@/features/runtime-test/engineLoader'
import type CustomUIData from '@/model/CustomUI/CustomUIData'
import type MainProject from '@/model/MainProject'
import EventPicShow from '@/model/EventClass/EventPicShow'
import EventStartBGM from '@/model/EventClass/EventStartBGM'
import EventStartSE from '@/model/EventClass/EventStartSE'
import EventStartVoice from '@/model/EventClass/EventStartVoice'
import EventStartBGS from '@/model/EventClass/EventStartBGS'
import EventID from '@/model/EventClass/EventID'
import { AVG_CANVAS_RESOURCE_MIME } from '@/features/virtual-resource/constants'
import { resolveEnginePathFromRelativePath } from '@/features/virtual-resource/rules'
import { IFBranch, inferIfBranch } from '@/control/eventDescriptionInferIf'
import { resolveResourceUrlFromFileName } from '@/features/virtual-resource/fileNamePath'
import {
  createOafAnimationPlayer,
  drawOafFrameToCanvas,
  fetchOafPreviewData,
  getOafFrameIndex,
  type OafAnimationPlayer,
} from '@/features/oaf-preview/runtime'
import type { PreviewTestSession } from '@/features/runtime-test/buildPreviewTestSession'
import { releaseSafariAudioObjectUrls } from '@/features/audio-playback/safariAudioPlayback'
import { useSceneEditorStore } from '@/store/sceneEditor'
import { useWorkspaceStore } from '@/store/workspace'
import {
  ensureBtnListIndexForDroppedGraphic,
  formatButtonChoiceEntry,
  resolveButtonOptionStoredPath,
} from '@/features/preview/canvasButtonDrop'
import {
  CANVAS_AUDIO_DROP_COLUMNS,
  CANVAS_AUDIO_DROP_ZONE_COUNT,
  CANVAS_IMAGE_DROP_COLUMNS,
  CANVAS_IMAGE_DROP_ROWS,
  CANVAS_IMAGE_DROP_ZONE_COUNT,
  audioDropZones,
  buttonDropZones,
  canCreateAudioEventFromDrop,
  getCanvasDropTopFolder,
  imageDropZones,
  isButtonGraphicDrop,
  normalizeCanvasDropItem,
  type CanvasResourceDragPayload,
  type CanvasResourceDropPayload,
} from '@/features/preview/canvasDropZones'
import { clampPreviewPoint, toDesignPoint } from '@/features/preview/imageGeometry'
import { EVENT_PALETTE } from '@/events/palette/eventPalette'

const props = withDefaults(defineProps<{
  testSession?: PreviewTestSession | null
}>(), {
  testSession: null,
})

const emit = defineEmits<{
  stopTest: []
}>()

const workspace = useWorkspaceStore()
const editor = useSceneEditorStore()
const message = useMessage()

const previewSurfaceRef = ref<{
  hostEl: HTMLElement | null
  stageEl: HTMLElement | null
  stageSize?: { width: number; height: number }
} | null>(null)
const previewCanvasRef = ref<HTMLCanvasElement | null>(null)
const previewTestFrameRef = ref<HTMLIFrameElement | null>(null)
let resizeObserver: ResizeObserver | null = null
let previewImage: HTMLImageElement | null = null
let previewVideo: HTMLVideoElement | null = null
let previewVideoFrameHandle = 0
let previewMainBufferCanvas: HTMLCanvasElement | null = null
let previewOafFrameHandle = 0
let previewOafRunToken = 0
let previewOafPlayer: OafImagePreviewPlayer | null = null
let previewOafUrl = ''
let previewOafLoadingUrl = ''
let previewOafStartedAt = 0
let previewOafLastFrameIndex = -1
let lastCanvasDropSignature = ''
let lastCanvasDropAt = 0
let previewTestRunToken = 0
let activePreviewTestCleanupUrls: string[] = []
let previewTestResizeRaf = 0
const previewCanvasCssSize = ref({ width: 1, height: 1 })
const previewOafImageSrc = ref('')
const currentProject = computed(() => (workspace.project as MainProject | null) ?? null)
const activeTab = ref<'preview' | 'test'>(props.testSession ? 'test' : 'preview')

const DIALOG_GRAPHIC_BASE_FOLDER = '图片/UI'
const OTHER_GRAPHIC_BASE_FOLDER = '图片/其他'
const IMAGE_OPTION_GRAPHIC_BASE_FOLDER = '图片/button'

type ActiveVisualPreview =
  | {
      kind: 'none'
      url: string
      name: string
      resourceUrl: string
      isSkinResource: boolean
      token: number
    }
  | {
      kind: 'image'
      url: string
      name: string
      resourceUrl: string
      isSkinResource: boolean
      token: number
    }
  | {
      kind: 'video'
      url: string
      name: string
      resourceUrl: string
      isSkinResource: boolean
      token: number
    }
  | {
      kind: 'oaf'
      url: string
      name: string
      resourceUrl: string
      isSkinResource: boolean
      token: number
    }

type OafImagePreviewPlayer = {
  player: OafAnimationPlayer
  frameUrls: string[]
}

type MainCanvasSurface = {
  canvas: HTMLCanvasElement
  displayCtx: CanvasRenderingContext2D
  bufferCanvas: HTMLCanvasElement
  bufferCtx: CanvasRenderingContext2D
  cssW: number
  cssH: number
}

const previewDropResource = ref<CanvasResourceDragPayload | null>(null)
const previewDropZoneIndex = ref(-1)

const isButtonGraphicCanvasDrop = computed(() => {
  const payload = previewDropResource.value
  return payload ? isButtonGraphicDrop(payload) : false
})

const isCanvasDropAvailable = computed(() => {
  const payload = previewDropResource.value
  if (!payload) return false
  if (!editor.selectedMap) return false
  if (payload.categoryKey === 'graphic') return true
  if (payload.categoryKey === 'audio') return canCreateAudioEventFromDrop(payload)
  return false
})

const showImageDropZones = computed(
  () => previewDropResource.value?.categoryKey === 'graphic' && !isButtonGraphicCanvasDrop.value,
)
const showButtonDropZones = computed(() => isButtonGraphicCanvasDrop.value)
const showAudioDropZones = computed(
  () => previewDropResource.value?.categoryKey === 'audio' && isCanvasDropAvailable.value,
)

const showCanvasDropUnavailable = computed(
  () => {
    if (!previewDropResource.value) return false
    if (previewDropResource.value.categoryKey === 'graphic') {
      return previewDropResource.value.source !== 'touch' && previewDropZoneIndex.value < 0
    }
    return !isCanvasDropAvailable.value
  },
)

function getPreviewCanvasHostElement() {
  return previewSurfaceRef.value?.hostEl ?? null
}

function getPreviewCanvasStageElement() {
  return previewSurfaceRef.value?.stageEl ?? previewCanvasRef.value
}

function handlePreviewSurfaceStageResize(size: { width: number; height: number }) {
  previewCanvasCssSize.value = {
    width: Math.max(1, Math.round(size.width || 1)),
    height: Math.max(1, Math.round(size.height || 1)),
  }
}

/** 工程设计分辨率。所有预览都会先按这个逻辑尺寸计算，再等比缩放到当前面板。 */
const previewDesignSize = computed(() => {
  const p = currentProject.value
  const w = p?.screenHash?.width ?? 1280
  const h = p?.screenHash?.height ?? 720
  return { width: Math.max(1, w), height: Math.max(1, h) }
})

const activeCanvasDesignSize = computed(() => {
  if (isTestTabActive.value && props.testSession) {
    return {
      width: Math.max(1, Math.round(props.testSession.bootInfo.canvasWidth || 1)),
      height: Math.max(1, Math.round(props.testSession.bootInfo.canvasHeight || 1)),
    }
  }
  return previewDesignSize.value
})

const emptyPreview: Extract<ActiveVisualPreview, { kind: 'none' }> = {
  kind: 'none',
  url: '',
  name: '',
  resourceUrl: '',
  isSkinResource: false,
  token: 0,
}

const resourceCanvasPreview = computed(() => workspace.resourceCanvasPreview ?? emptyPreview)

function getWorkspaceCanvasDragResource(): CanvasResourceDragPayload | null {
  const payload = workspace.canvasDragResource
  if (!payload) return null
  const item = normalizeCanvasDropItem(payload)
  if (!item) return null
  return {
    ...item,
    source: payload.source === 'touch' ? 'touch' : 'native',
  }
}

function clearWorkspaceCanvasDragResource() {
  workspace.clearCanvasDragResource()
}

function joinEventResourcePath(mappingPath: string, relativePath: string): string {
  return resolveEnginePathFromRelativePath('graphic', mappingPath, relativePath)
}

function resolveGraphicResourceUrl(storedPath: string): string {
  return resolveResourceUrlFromFileName(
    workspace.virtualResourceRoots.graphic,
    'graphic',
    storedPath,
    workspace.resourcePreviewOverrides,
  )
}

function resolveUiGraphicResourceUrl(storedPath: string): string {
  return resolveResourceUrlFromFileName(
    workspace.virtualResourceRoots.graphic,
    'graphic',
    storedPath,
    workspace.resourcePreviewOverrides,
    DIALOG_GRAPHIC_BASE_FOLDER,
  )
}

function resolveOtherGraphicResourceUrl(storedPath: string): string {
  return resolveResourceUrlFromFileName(
    workspace.virtualResourceRoots.graphic,
    'graphic',
    storedPath,
    workspace.resourcePreviewOverrides,
    OTHER_GRAPHIC_BASE_FOLDER,
  )
}

function resolveImageOptionGraphicResourceUrl(storedPath: string): string {
  return resolveResourceUrlFromFileName(
    workspace.virtualResourceRoots.graphic,
    'graphic',
    storedPath,
    workspace.resourcePreviewOverrides,
    IMAGE_OPTION_GRAPHIC_BASE_FOLDER,
  )
}

const isPreviewTabActive = computed(() => activeTab.value === 'preview')
const isTestTabActive = computed(() => activeTab.value === 'test')
const isTestRunning = computed(() => !!props.testSession)

const showEventWebglPreview = computed(() => {
  return isPreviewTabActive.value && resourceCanvasPreview.value.kind === 'none'
})
const showCustomUiPreview = computed(() => {
  return isPreviewTabActive.value && resourceCanvasPreview.value.kind === 'none' && !!editor.selectedCustomUi
})
const selectedCustomUiPreviewData = computed(() => editor.selectedCustomUiData as CustomUIData | null)

const previewSelectedEventIndex = computed(() => {
  if (editor.selectedEventIndex !== null) return editor.selectedEventIndex
  const previousIndex = editor.selectedAddPlaceholder?.previousIndex
  return typeof previousIndex === 'number' && previousIndex >= 0 ? previousIndex : null
})

function parseIfMouseRect(value: string) {
  const [x = '0', y = '0', width = '100', height = '100'] = String(value ?? '').split(',')
  const parsePart = (raw: string, fallback: number) => {
    const parsed = Number.parseInt(raw ?? '', 10)
    return Number.isFinite(parsed) ? parsed : fallback
  }
  return {
    x: parsePart(x, 0),
    y: parsePart(y, 0),
    width: Math.max(0, parsePart(width, 100)),
    height: Math.max(0, parsePart(height, 100)),
  }
}

const previewMouseRectOverlayStyle = computed(() => {
  editor.scenePreviewRevision
  const index = previewSelectedEventIndex.value
  const event = index === null ? null : editor.selectedSceneEvents[index] ?? null
  if (!showEventWebglPreview.value || !event || event.Code !== EventID.IF) return null
  const parameter = event.Parameter ?? []
  if (inferIfBranch(parameter) !== IFBranch.Mouse) return null
  if (Number.parseInt(parameter[1] ?? '0', 10) !== 0) return null
  const rect = parseIfMouseRect(parameter[2] ?? '0,0,100,100')
  const left = Math.max(0, rect.x * previewScale.value)
  const top = Math.max(0, rect.y * previewScale.value)
  const width = Math.max(0, rect.width * previewScale.value)
  const height = Math.max(0, rect.height * previewScale.value)
  return {
    left: `${left}px`,
    top: `${top}px`,
    width: `${width}px`,
    height: `${height}px`,
  }
})

const previewResourceSignature = computed(() => {
  const overrides = Object.entries(workspace.resourcePreviewOverrides)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, value]) => `${key}=>${value}`)
  return overrides.join('|')
})

/**
 * 预览区全局缩放。
 * 统一由“当前 canvas 实际显示尺寸 / 游戏设计尺寸”推导，所有预览元素都只认这一个缩放值。
 */
const previewScale = computed(() => {
  const { width: designW, height: designH } = previewDesignSize.value
  return Math.min(previewCanvasCssSize.value.width / designW, previewCanvasCssSize.value.height / designH)
})

function scalePreviewValue(value: number): number {
  return Math.max(0, Math.round(value * Math.max(0.01, previewScale.value)))
}
/** 资源管理器中的资源预览优先级高于事件预览。 */
const activeVisualPreview = computed<ActiveVisualPreview>(() => {
  if (!isPreviewTabActive.value) {
    return emptyPreview
  }
  const resourcePreview = resourceCanvasPreview.value
  if (resourcePreview.kind !== 'none' && resourcePreview.url) {
    return resourcePreview
  }
  return emptyPreview
})

const previewSurfaceBackgroundUrl = computed(() =>
  activeVisualPreview.value.kind === 'oaf' ? previewOafImageSrc.value : '',
)

/** 优先从运行时缓存读取拖拽数据，失败时再回退到浏览器 dataTransfer。 */
function parseCanvasResourceDrop(event: DragEvent): CanvasResourceDragPayload | null {
  const liveDragPayload = getWorkspaceCanvasDragResource()
  if (liveDragPayload) return liveDragPayload
  const raw = event.dataTransfer?.getData(AVG_CANVAS_RESOURCE_MIME)?.trim()
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<CanvasResourceDropPayload>
    const normalized = normalizeCanvasDropItem(parsed)
    if (!normalized) return null
    return {
      ...normalized,
      source: 'native',
    }
  } catch {
    return null
  }
}

/** 国产浏览器可能不暴露自定义 dataTransfer，内部资源拖拽统一由工具接管。 */
function acceptCanvasResourceDrag(event: DragEvent) {
  event.preventDefault()
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'copy'
  }
}

function clearCanvasDropState() {
  previewDropResource.value = null
  previewDropZoneIndex.value = -1
  clearWorkspaceCanvasDragResource()
}

const touchCanvasDragResource = computed(() => {
  const payload = getWorkspaceCanvasDragResource()
  return payload?.source === 'touch' ? payload : null
})

/** 避免在拖拽经过子节点时，把 still-inside 的状态误判成离开画布。 */
function isPointerInsideCanvasHost(event: DragEvent): boolean {
  const host = getPreviewCanvasHostElement()
  if (!host) return false
  const rect = host.getBoundingClientRect()
  return (
    event.clientX >= rect.left
    && event.clientX <= rect.right
    && event.clientY >= rect.top
    && event.clientY <= rect.bottom
  )
}

/** 把画布分成 5x5 区块，仅前 23 个区块可用于创建图片事件。 */
function getImageDropZoneIndex(event: DragEvent): number {
  const stage = getPreviewCanvasStageElement()
  if (!stage) return 0
  const rect = stage.getBoundingClientRect()
  if (rect.width <= 0 || rect.height <= 0) return 0
  const relativeX = Math.max(0, Math.min(rect.width - 1, event.clientX - rect.left))
  const relativeY = Math.max(0, Math.min(rect.height - 1, event.clientY - rect.top))
  const col = Math.max(0, Math.min(CANVAS_IMAGE_DROP_COLUMNS - 1, Math.floor((relativeX / rect.width) * CANVAS_IMAGE_DROP_COLUMNS)))
  const row = Math.max(0, Math.min(CANVAS_IMAGE_DROP_ROWS - 1, Math.floor((relativeY / rect.height) * CANVAS_IMAGE_DROP_ROWS)))
  const zoneIndex = row * CANVAS_IMAGE_DROP_COLUMNS + col
  return zoneIndex < CANVAS_IMAGE_DROP_ZONE_COUNT ? zoneIndex : -1
}

/** 音频拖拽时显示 1x4 分区，对应 4 个音轨落点。 */
function getAudioDropZoneIndex(event: DragEvent): number {
  const stage = getPreviewCanvasStageElement()
  if (!stage) return -1
  const rect = stage.getBoundingClientRect()
  if (rect.width <= 0 || rect.height <= 0) return -1
  const relativeX = Math.max(0, Math.min(rect.width - 1, event.clientX - rect.left))
  const col = Math.max(0, Math.min(CANVAS_AUDIO_DROP_COLUMNS - 1, Math.floor((relativeX / rect.width) * CANVAS_AUDIO_DROP_COLUMNS)))
  return col < CANVAS_AUDIO_DROP_ZONE_COUNT ? col : -1
}

/** 按钮图拖入时整屏为 1 个落点分区。 */
function getButtonDropZoneIndex(event: DragEvent): number {
  const stage = getPreviewCanvasStageElement()
  if (!stage) return -1
  const rect = stage.getBoundingClientRect()
  if (rect.width <= 0 || rect.height <= 0) return -1
  if (event.clientX < rect.left || event.clientX >= rect.right) return -1
  if (event.clientY < rect.top || event.clientY >= rect.bottom) return -1
  return 0
}

function getCanvasDropZoneIndex(payload: CanvasResourceDragPayload, event: DragEvent): number {
  if (payload.categoryKey === 'graphic' && isButtonGraphicDrop(payload)) return getButtonDropZoneIndex(event)
  if (payload.categoryKey === 'graphic') return getImageDropZoneIndex(event)
  if (payload.categoryKey === 'audio') return getAudioDropZoneIndex(event)
  return -1
}

/** 将拖拽落点转换为游戏设计坐标，供图片选项 ButtonChoice 的 x,y 使用。 */
function getCanvasDropDesignPoint(event: DragEvent): { x: number; y: number } {
  const stage = getPreviewCanvasStageElement()
  if (!stage) return { x: 0, y: 0 }
  const rect = stage.getBoundingClientRect()
  const screenPoint = {
    x: Math.max(0, event.clientX - rect.left),
    y: Math.max(0, event.clientY - rect.top),
  }
  const designPoint = toDesignPoint(screenPoint, previewScale.value)
  const { width, height } = previewDesignSize.value
  return clampPreviewPoint(designPoint, width, height)
}

const buttonDifPaletteItem = EVENT_PALETTE.flatMap((group) => group.items).find(
  (item) => item.eventCode === EventID.ButtonDif,
)

/** 图像/按钮 目录资源拖入：写入 btnList 并创建图片选项块（单选项，坐标为落点）。 */
function createButtonDifEventFromDrop(
  payload: CanvasResourceDropPayload,
  zoneIndex: number,
  event: DragEvent,
): boolean {
  if (zoneIndex < 0) return false
  const project = currentProject.value
  if (!project) {
    console.error('[canvas-drop] 当前工程不存在，无法创建图片选项')
    return false
  }
  if (!buttonDifPaletteItem) {
    console.error('[canvas-drop] 事件面板未配置图片选项（ButtonDif）')
    return false
  }
  const storedPath = resolveButtonOptionStoredPath(payload)
  if (!storedPath) {
    console.error('[canvas-drop] 按钮资源路径为空', payload)
    return false
  }
  const displayName = payload.name.trim() || storedPath.split('/').pop() || ''
  const buttonIndex = ensureBtnListIndexForDroppedGraphic(project, storedPath, displayName)
  if (buttonIndex === null) return false
  workspace.markProjectDirty()
  const { x, y } = getCanvasDropDesignPoint(event)
  const inserted = editor.insertEventFromPalette(buttonDifPaletteItem, { insertAfterChoiceBlock: true })
  if (!inserted) return false
  const headerIndex = editor.selectedSceneEvents.indexOf(inserted)
  if (headerIndex < 0) return false
  return editor.syncButtonChoiceBlock(headerIndex, [formatButtonChoiceEntry(buttonIndex, x, y)])
}

/** 所有拖拽创建事件都走 sceneEditor 的统一插入入口。 */
function insertCanvasEvent(
  event: EventPicShow | EventStartBGM | EventStartSE | EventStartVoice | EventStartBGS,
): boolean {
  return editor.insertEventObject(event) != null
}

/** 图片拖入预览区时，根据落点分区创建 `显示图片` 事件。 */
function createImageEventFromDrop(payload: CanvasResourceDropPayload, zoneIndex: number): boolean {
  if (zoneIndex < 0 || zoneIndex >= CANVAS_IMAGE_DROP_ZONE_COUNT) return false
  const event = new EventPicShow()
  event.PicId = zoneIndex + 1
  event.PicPath = joinEventResourcePath(payload.mappingPath, payload.relativePath)
  event.IsPublicwebdisk = true
  return insertCanvasEvent(event)
}

/** 音频拖入预览区时，根据资源目录自动匹配音频事件类型。 */
function createAudioEventFromDrop(payload: CanvasResourceDropPayload, zoneIndex: number): boolean {
  const audioIndex = Math.max(0, Math.min(3, zoneIndex))
  switch (getCanvasDropTopFolder(payload)) {
    case '音乐': {
      const event = new EventStartBGM()
      event.Path = resolveEnginePathFromRelativePath('audio', payload.mappingPath, payload.relativePath)
      event.AudioIndex = audioIndex
      event.IsPublicwebdisk = true
      return insertCanvasEvent(event)
    }
    case '音效': {
      const event = new EventStartSE()
      event.Path = resolveEnginePathFromRelativePath('audio', payload.mappingPath, payload.relativePath)
      event.AudioIndex = audioIndex
      event.IsPublicwebdisk = true
      return insertCanvasEvent(event)
    }
    case '语音': {
      const event = new EventStartVoice()
      event.Path = resolveEnginePathFromRelativePath('audio', payload.mappingPath, payload.relativePath)
      event.AudioIndex = audioIndex
      event.IsPublicwebdisk = true
      return insertCanvasEvent(event)
    }
    case '循环音效': {
      const event = new EventStartBGS()
      event.Path = resolveEnginePathFromRelativePath('audio', payload.mappingPath, payload.relativePath)
      event.AudioIndex = audioIndex
      event.IsPublicwebdisk = true
      return insertCanvasEvent(event)
    }
    default:
      return false
  }
}

/** 拖拽悬停时只更新高亮区，不在这个阶段创建事件。 */
function handleCanvasDragOver(event: DragEvent) {
  if (!isPreviewTabActive.value) {
    clearCanvasDropState()
    return
  }
  const payload = parseCanvasResourceDrop(event)
  if (!payload) {
    clearCanvasDropState()
    return
  }
  acceptCanvasResourceDrag(event)
  previewDropResource.value = payload
  previewDropZoneIndex.value = getCanvasDropZoneIndex(payload, event)
}

function handleCanvasDragEnter(event: DragEvent) {
  if (!isPreviewTabActive.value) return
  const payload = parseCanvasResourceDrop(event)
  if (!payload) return
  acceptCanvasResourceDrag(event)
  previewDropResource.value = payload
  previewDropZoneIndex.value = getCanvasDropZoneIndex(payload, event)
}

function handleCanvasDragLeave(event: DragEvent) {
  if (!isPreviewTabActive.value) return
  if (isPointerInsideCanvasHost(event)) return
  clearCanvasDropState()
}

/** 真正落下时才创建事件，并做短时间去重，避免浏览器重复触发 drop。 */
function commitCanvasDrop(payload: CanvasResourceDragPayload, zoneIndex: number, event: DragEvent) {
  clearCanvasDropState()
  const isButtonDrop = isButtonGraphicDrop(payload)
  let dropZoneKey = String(zoneIndex)
  if (isButtonDrop) {
    const { x, y } = getCanvasDropDesignPoint(event)
    dropZoneKey = `button:${x},${y}`
  }
  const dropSignature = [
    editor.selectedMap?.Id ?? 'none',
    `${payload.categoryKey}:${payload.relativePath}`,
    dropZoneKey,
  ].join('|')
  const now = Date.now()
  if (lastCanvasDropSignature === dropSignature && now - lastCanvasDropAt < 400) {
    return
  }
  lastCanvasDropSignature = dropSignature
  lastCanvasDropAt = now
  if (!editor.selectedMap) {
    message.warning('请先在左侧选择一个剧情，再拖入资源')
    return
  }
  const ok = isButtonDrop
    ? zoneIndex >= 0 && createButtonDifEventFromDrop(payload, zoneIndex, event)
    : payload.categoryKey === 'graphic'
      ? createImageEventFromDrop(payload, zoneIndex)
      : payload.categoryKey === 'audio'
        ? zoneIndex >= 0 && createAudioEventFromDrop(payload, zoneIndex)
        : false
  if (!ok) {
    message.warning('当前资源类型暂不支持拖入预览区自动创建事件')
  }
}

function handleCanvasDrop(event: DragEvent) {
  if (!isPreviewTabActive.value) {
    clearCanvasDropState()
    return
  }
  const payload = parseCanvasResourceDrop(event)
  if (!payload) return
  acceptCanvasResourceDrag(event)
  event.stopPropagation()
  const hoveredZoneIndex = previewDropZoneIndex.value
  const computedZoneIndex = getCanvasDropZoneIndex(payload, event)
  const zoneIndex = hoveredZoneIndex >= 0 ? hoveredZoneIndex : computedZoneIndex
  commitCanvasDrop(payload, zoneIndex, event)
}

function handleCanvasPendingAddPointerDown(event: PointerEvent) {
  if (!isPreviewTabActive.value) return
  const payload = touchCanvasDragResource.value
  if (!payload) return
  const syntheticEvent = {
    clientX: event.clientX,
    clientY: event.clientY,
  } as DragEvent
  event.preventDefault()
  event.stopPropagation()
  previewDropResource.value = payload
  const zoneIndex = getCanvasDropZoneIndex(payload, syntheticEvent)
  if (zoneIndex < 0) {
    clearCanvasDropState()
    return
  }
  commitCanvasDrop(payload, zoneIndex, syntheticEvent)
}

function handleWindowPendingAddPointerDown(event: PointerEvent) {
  if (!touchCanvasDragResource.value) return
  const host = getPreviewCanvasHostElement()
  if (host && event.target instanceof Node && host.contains(event.target)) return
  clearCanvasDropState()
}

/** 根据宿主容器和设计分辨率，计算当前应显示的画布 CSS 尺寸。 */
function getCanvasCssSize() {
  const cssW = Math.max(1, Math.round(previewCanvasCssSize.value.width || 1))
  const cssH = Math.max(1, Math.round(previewCanvasCssSize.value.height || 1))
  previewCanvasCssSize.value = { width: cssW, height: cssH }
  return { cssW, cssH }
}

function ensureMainCanvasSurface(): MainCanvasSurface | null {
  const canvas = previewCanvasRef.value
  const size = getCanvasCssSize()
  if (!canvas || !size) return null
  const dpr = window.devicePixelRatio || 1

  canvas.style.width = `${size.cssW}px`
  canvas.style.height = `${size.cssH}px`
  const pixelWidth = Math.max(1, Math.round(size.cssW * dpr))
  const pixelHeight = Math.max(1, Math.round(size.cssH * dpr))
  if (canvas.width !== pixelWidth) canvas.width = pixelWidth
  if (canvas.height !== pixelHeight) canvas.height = pixelHeight

  const displayCtx = canvas.getContext('2d')
  if (!displayCtx) return null

  const bufferCanvas = previewMainBufferCanvas ?? document.createElement('canvas')
  previewMainBufferCanvas = bufferCanvas
  if (bufferCanvas.width !== pixelWidth) bufferCanvas.width = pixelWidth
  if (bufferCanvas.height !== pixelHeight) bufferCanvas.height = pixelHeight

  const bufferCtx = bufferCanvas.getContext('2d')
  if (!bufferCtx) return null
  bufferCtx.setTransform(1, 0, 0, 1, 0, 0)
  bufferCtx.clearRect(0, 0, bufferCanvas.width, bufferCanvas.height)
  bufferCtx.scale(dpr, dpr)

  return {
    canvas,
    displayCtx,
    bufferCanvas,
    bufferCtx,
    cssW: size.cssW,
    cssH: size.cssH,
  }
}

function fillMainCanvasBlack(surface: MainCanvasSurface) {
  surface.bufferCtx.fillStyle = '#000000'
  surface.bufferCtx.fillRect(0, 0, surface.cssW, surface.cssH)
}

function presentMainCanvas(surface: MainCanvasSurface) {
  surface.displayCtx.setTransform(1, 0, 0, 1, 0, 0)
  surface.displayCtx.clearRect(0, 0, surface.canvas.width, surface.canvas.height)
  surface.displayCtx.drawImage(surface.bufferCanvas, 0, 0)
}

/** 清理主画布，始终回到黑底。 */
function clearCanvas() {
  const surface = ensureMainCanvasSurface()
  if (!surface) return
  fillMainCanvasBlack(surface)
  presentMainCanvas(surface)
}

function revokePreviewTestCleanupUrls() {
  releaseSafariAudioObjectUrls(activePreviewTestCleanupUrls)
  activePreviewTestCleanupUrls = []
}

function stopPreviewTestRuntime() {
  previewTestRunToken += 1
  stopPreviewTestEngineInFrame(previewTestFrameRef.value)
  revokePreviewTestCleanupUrls()
  if (previewTestResizeRaf) {
    cancelAnimationFrame(previewTestResizeRaf)
    previewTestResizeRaf = 0
  }
}

function syncTestCanvasSize() {
  getCanvasCssSize()
  if (isTestTabActive.value) {
    notifyPreviewTestEngineResize(previewTestFrameRef.value)
  }
}

function scheduleSyncTestCanvasSize() {
  if (previewTestResizeRaf) {
    cancelAnimationFrame(previewTestResizeRaf)
  }
  previewTestResizeRaf = requestAnimationFrame(() => {
    previewTestResizeRaf = 0
    syncTestCanvasSize()
  })
}

async function applyPreviewTestSession(session: PreviewTestSession | null) {
  previewTestRunToken += 1
  const currentToken = previewTestRunToken

  stopPreviewTestEngineInFrame(previewTestFrameRef.value)
  revokePreviewTestCleanupUrls()

  await nextTick()
  if (currentToken !== previewTestRunToken || !isTestTabActive.value) return

  syncTestCanvasSize()
  if (!session) return

  try {
    const frame = previewTestFrameRef.value
    if (!frame) return
    activePreviewTestCleanupUrls = session.cleanupObjectUrls.slice()
    await startPreviewTestEngineInFrame({ frame, session })
    if (currentToken !== previewTestRunToken || !isTestTabActive.value) {
      stopPreviewTestEngineInFrame(frame)
    }
  } catch (error) {
    if (currentToken !== previewTestRunToken) return
    message.error(error instanceof Error ? error.message : '启动测试失败')
    emit('stopTest')
  }
}

/** 资源预览：能完整放下时按原图尺寸居中，否则等比缩小后居中（不放大）。 */
function drawContain(source: CanvasImageSource, sourceWidth: number, sourceHeight: number) {
  const surface = ensureMainCanvasSurface()
  if (!surface || sourceWidth <= 0 || sourceHeight <= 0) return
  fillMainCanvasBlack(surface)
  const ctx = surface.bufferCtx

  const scale = Math.min(1, surface.cssW / sourceWidth, surface.cssH / sourceHeight)
  const drawW = Math.max(1, Math.floor(sourceWidth * scale))
  const drawH = Math.max(1, Math.floor(sourceHeight * scale))
  const offsetX = Math.floor((surface.cssW - drawW) / 2)
  const offsetY = Math.floor((surface.cssH - drawH) / 2)
  ctx.drawImage(source, offsetX, offsetY, drawW, drawH)
  presentMainCanvas(surface)
}

/** 停止视频播放与逐帧绘制，防止 requestAnimationFrame 泄漏。 */
function stopVideoPreview() {
  if (previewVideoFrameHandle) {
    cancelAnimationFrame(previewVideoFrameHandle)
    previewVideoFrameHandle = 0
  }
  if (previewVideo) {
    previewVideo.pause()
    previewVideo.src = ''
    previewVideo.load()
    previewVideo = null
  }
}

function stopOafPreview() {
  previewOafRunToken += 1
  if (previewOafFrameHandle) {
    cancelAnimationFrame(previewOafFrameHandle)
    previewOafFrameHandle = 0
  }
  if (previewOafPlayer) {
    revokeOafFrameUrls(previewOafPlayer.frameUrls)
  }
  previewOafPlayer = null
  previewOafUrl = ''
  previewOafLoadingUrl = ''
  previewOafStartedAt = 0
  previewOafLastFrameIndex = -1
  previewOafImageSrc.value = ''
}

function revokeOafFrameUrls(urls: string[]) {
  for (const frameUrl of urls) {
    URL.revokeObjectURL(frameUrl)
  }
}

async function createCanvasObjectUrl(canvas: HTMLCanvasElement): Promise<string> {
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((result) => {
      if (result) {
        resolve(result)
        return
      }
      reject(new Error('OAF 帧图生成失败'))
    }, 'image/png')
  })
  return URL.createObjectURL(blob)
}

async function preloadImageUrl(url: string): Promise<void> {
  const image = new Image()
  image.src = url
  if (image.decode) {
    await image.decode()
    return
  }
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve()
    image.onerror = () => reject(new Error('OAF 帧图加载失败'))
  })
}

async function createOafFrameUrls(
  player: OafAnimationPlayer,
  onFrameReady?: (frameUrls: string[], frameIndex: number, player: OafAnimationPlayer) => void,
): Promise<string[]> {
  const frameCanvas = document.createElement('canvas')
  frameCanvas.width = Math.max(1, player.width)
  frameCanvas.height = Math.max(1, player.height)
  const frameCtx = frameCanvas.getContext('2d')
  if (!frameCtx) return []
  const frameUrls: string[] = []
  try {
    for (const frame of player.frames) {
      frameCtx.setTransform(1, 0, 0, 1, 0, 0)
      frameCtx.clearRect(0, 0, frameCanvas.width, frameCanvas.height)
      drawOafFrameToCanvas(frameCtx, frame)
      const frameUrl = await createCanvasObjectUrl(frameCanvas)
      await preloadImageUrl(frameUrl)
      frameUrls.push(frameUrl)
      onFrameReady?.(frameUrls, frameUrls.length - 1, player)
    }
    return frameUrls
  } catch (error) {
    revokeOafFrameUrls(frameUrls)
    throw error
  }
}

function updateOafImageFrame(resourcePlayer: OafImagePreviewPlayer, frameIndex: number, force = false) {
  if (!force && frameIndex === previewOafLastFrameIndex) return
  const frameUrl = resourcePlayer.frameUrls[frameIndex]
  if (!frameUrl) return
  previewOafImageSrc.value = frameUrl
  previewOafLastFrameIndex = frameIndex
}

async function createOafImagePreviewPlayer(
  url: string,
  onFirstFrameReady?: (player: OafImagePreviewPlayer) => void,
): Promise<OafImagePreviewPlayer | null> {
  const data = await fetchOafPreviewData(url)
  const player = await createOafAnimationPlayer(data, resolveGraphicResourceUrl)
  const imagePlayer: OafImagePreviewPlayer = {
    player,
    frameUrls: [],
  }
  const frameUrls = await createOafFrameUrls(player, (readyFrameUrls, frameIndex) => {
    imagePlayer.frameUrls = readyFrameUrls
    if (frameIndex === 0) {
      onFirstFrameReady?.(imagePlayer)
    }
  })
  if (!frameUrls.length) return null
  imagePlayer.frameUrls = frameUrls
  return imagePlayer
}

function runOafPreviewLoop(resourcePlayer: OafImagePreviewPlayer, startedAt: number, token: number) {
  previewOafFrameHandle = 0
  const frameCount = resourcePlayer.player.frames.length
  if (!frameCount || token !== previewOafRunToken) return
  const frameIndex = getOafFrameIndex(resourcePlayer.player, startedAt)
  updateOafImageFrame(resourcePlayer, frameIndex)
  previewOafFrameHandle = requestAnimationFrame(() => runOafPreviewLoop(resourcePlayer, startedAt, token))
}

function startOafPreview(url: string) {
  stopVideoPreview()
  previewImage = null
  if (previewOafUrl === url && !previewOafPlayer && previewOafLoadingUrl === url) return
  if (previewOafUrl === url && previewOafPlayer) {
    const player = previewOafPlayer
    const frameIndex = getOafFrameIndex(player.player, previewOafStartedAt)
    updateOafImageFrame(player, frameIndex, true)
    if (!previewOafFrameHandle) {
      const token = previewOafRunToken
      previewOafFrameHandle = requestAnimationFrame(() =>
        runOafPreviewLoop(player, previewOafStartedAt, token),
      )
    }
    return
  }
  stopOafPreview()
  previewOafUrl = url
  previewOafLoadingUrl = url
  clearCanvas()
  const token = ++previewOafRunToken
  void createOafImagePreviewPlayer(url, (player) => {
    if (token !== previewOafRunToken || previewOafUrl !== url) return
    previewOafPlayer = player
    previewOafStartedAt = performance.now()
    previewOafLastFrameIndex = -1
    updateOafImageFrame(player, 0, true)
  })
    .then((player) => {
      if (token !== previewOafRunToken || !player) {
        if (player) revokeOafFrameUrls(player.frameUrls)
        return
      }
      previewOafLoadingUrl = ''
      previewOafPlayer = player
      if (!previewOafStartedAt) {
        previewOafStartedAt = performance.now()
        previewOafLastFrameIndex = -1
        updateOafImageFrame(player, 0, true)
      }
      runOafPreviewLoop(player, previewOafStartedAt, token)
    })
    .catch(() => {
      if (token !== previewOafRunToken) return
      previewOafUrl = ''
      previewOafLoadingUrl = ''
      clearCanvas()
    })
}

function drawVideoFrame() {
  if (!previewVideo) return
  if (previewVideo.readyState >= 2) {
    drawContain(previewVideo, previewVideo.videoWidth, previewVideo.videoHeight)
  }
  previewVideoFrameHandle = requestAnimationFrame(drawVideoFrame)
}

/** 启动图片预览。 */
function startImagePreview(url: string) {
  stopVideoPreview()
  stopOafPreview()
  void getCachedImageResource(url)
    .then((img) => {
      previewImage = img
      drawContain(img, img.naturalWidth, img.naturalHeight)
    })
    .catch(() => {
      previewImage = null
      clearCanvas()
    })
}

/** 启动视频预览。 */
function startVideoPreview(url: string) {
  stopVideoPreview()
  stopOafPreview()
  previewImage = null
  const video = document.createElement('video')
  video.src = url
  video.muted = true
  video.autoplay = true
  video.loop = true
  video.playsInline = true
  previewVideo = video
  void video.play().catch(() => {
    /* ignore autoplay failure */
  })
  video.addEventListener(
    'loadeddata',
    () => {
      drawVideoFrame()
    },
    { once: true },
  )
}

/** 根据容器大小等比缩放画布，并在需要时重绘资源预览。 */
function updateLayout() {
  if (!isPreviewTabActive.value) {
    return
  }
  const preview = activeVisualPreview.value
  if (preview.kind === 'image' && preview.url) {
    if (previewImage && previewImage.src === preview.url && previewImage.complete) {
      drawContain(previewImage, previewImage.naturalWidth, previewImage.naturalHeight)
      return
    }
    startImagePreview(preview.url)
    return
  }
  if (preview.kind === 'video' && preview.url) {
    if (previewVideo && previewVideo.src === preview.url) return
    startVideoPreview(preview.url)
    return
  }
  if (preview.kind === 'oaf' && preview.url) {
    startOafPreview(preview.url)
    return
  }
  previewImage = null
  stopVideoPreview()
  stopOafPreview()
  clearCanvas()
}

function bindResizeObserver() {
  resizeObserver?.disconnect()
  resizeObserver = null
  const el = getPreviewCanvasHostElement()
  if (!el || typeof ResizeObserver === 'undefined') return
  resizeObserver = new ResizeObserver(() => {
    if (isTestTabActive.value) {
      scheduleSyncTestCanvasSize()
      return
    }
    updateLayout()
  })
  resizeObserver.observe(el)
}

watch(previewDesignSize, () => {
  if (isTestTabActive.value) {
    void nextTick(() => scheduleSyncTestCanvasSize())
    return
  }
  void nextTick(() => updateLayout())
})

watch(activeVisualPreview, () => {
  void nextTick(() => updateLayout())
})

watch(
  () => props.testSession?.id ?? 0,
  (sessionId) => {
    if (sessionId) {
      activeTab.value = 'test'
      return
    }
    if (activeTab.value === 'test') {
      activeTab.value = 'preview'
    }
  },
)

watch(activeTab, (nextTab) => {
  clearCanvasDropState()
  if (nextTab !== 'preview') {
    stopVideoPreview()
    stopOafPreview()
    previewImage = null
    void nextTick(() => {
      scheduleSyncTestCanvasSize()
    })
    return
  }
  stopPreviewTestRuntime()
  void nextTick(() => {
    updateLayout()
  })
})

watch(
  [touchCanvasDragResource, () => activeTab.value],
  ([payload, tab]) => {
    if (tab !== 'preview') return
    if (payload) {
      previewDropResource.value = payload
      previewDropZoneIndex.value = -1
      return
    }
    if (previewDropResource.value?.source === 'touch') {
      previewDropResource.value = null
      previewDropZoneIndex.value = -1
    }
  },
  { immediate: true },
)

watch(
  [() => props.testSession?.id ?? 0, () => activeTab.value],
  () => {
    if (activeTab.value !== 'test') {
      stopPreviewTestRuntime()
      return
    }
    void applyPreviewTestSession(props.testSession ?? null)
  },
)

onMounted(() => {
  window.addEventListener('pointerdown', handleWindowPendingAddPointerDown, true)
  void nextTick(() => {
    bindResizeObserver()
    if (isTestTabActive.value) {
      void applyPreviewTestSession(props.testSession ?? null)
      return
    }
    void updateLayout()
  })
})

onBeforeUnmount(() => {
  window.removeEventListener('pointerdown', handleWindowPendingAddPointerDown, true)
  stopVideoPreview()
  stopOafPreview()
  stopPreviewTestRuntime()
  previewImage = null
  previewMainBufferCanvas = null
  resizeObserver?.disconnect()
  resizeObserver = null
})
</script>

<template>
  <div class="preview-block" @contextmenu.prevent @dblclick.prevent>
    <div class="preview-placeholder">
      <div class="preview-viewport-tag">
        <PreviewSurface
          ref="previewSurfaceRef"
          :host-class="['preview-canvas-host', { 'preview-canvas-host-dropping': isPreviewTabActive && !!previewDropResource }]"
          stage-class="preview-canvas-stage"
          :design-width="activeCanvasDesignSize.width"
          :design-height="activeCanvasDesignSize.height"
          :background-url="previewSurfaceBackgroundUrl"
          background-class="preview-oaf-background"
          @pointerdown="handleCanvasPendingAddPointerDown"
          @dragenter="handleCanvasDragEnter"
          @dragover="handleCanvasDragOver"
          @dragleave="handleCanvasDragLeave"
          @drop="handleCanvasDrop"
          @stage-resize="handlePreviewSurfaceStageResize"
        >
          <WebGLPreviewRenderer
            v-show="showEventWebglPreview"
            :events="showCustomUiPreview ? [] : editor.selectedSceneEvents"
            :selected-index="showCustomUiPreview ? null : previewSelectedEventIndex"
            :revision="showCustomUiPreview ? editor.customUiRevision : editor.scenePreviewRevision"
            :resource-signature="previewResourceSignature"
            :design-width="previewDesignSize.width"
            :design-height="previewDesignSize.height"
            :stage-width="previewCanvasCssSize.width"
            :stage-height="previewCanvasCssSize.height"
            :project="currentProject"
            :custom-ui-data="showCustomUiPreview ? selectedCustomUiPreviewData : null"
            :custom-ui-revision="editor.customUiRevision"
            :resolve-graphic-resource-url="resolveGraphicResourceUrl"
            :resolve-ui-graphic-resource-url="resolveUiGraphicResourceUrl"
            :resolve-other-graphic-resource-url="resolveOtherGraphicResourceUrl"
            :resolve-image-option-graphic-resource-url="resolveImageOptionGraphicResourceUrl"
          />
          <canvas
            ref="previewCanvasRef"
            class="preview-game-canvas"
            v-show="isPreviewTabActive && !showEventWebglPreview && activeVisualPreview.kind !== 'oaf'"
            aria-label="画面预览"
          />
          <iframe
            ref="previewTestFrameRef"
            class="preview-test-frame"
            v-show="isTestTabActive"
            title="作品测试画面"
          />
          <div
            v-if="previewMouseRectOverlayStyle"
            class="preview-mouse-rect-overlay"
            :style="previewMouseRectOverlayStyle"
          />
          <CanvasEditOverlay
            v-if="showEventWebglPreview && !previewDropResource"
            :events="showCustomUiPreview ? [] : editor.selectedSceneEvents"
            :selected-index="showCustomUiPreview ? null : previewSelectedEventIndex"
            :revision="showCustomUiPreview ? editor.customUiRevision : editor.scenePreviewRevision"
            :project="currentProject"
            :stage-width="previewCanvasCssSize.width"
            :stage-height="previewCanvasCssSize.height"
            :preview-scale="previewScale"
            :custom-ui-data="showCustomUiPreview ? selectedCustomUiPreviewData : null"
            :custom-ui-revision="editor.customUiRevision"
            :resolve-graphic-resource-url="resolveGraphicResourceUrl"
            :resolve-ui-graphic-resource-url="resolveUiGraphicResourceUrl"
            :resolve-other-graphic-resource-url="resolveOtherGraphicResourceUrl"
            :resolve-image-option-graphic-resource-url="resolveImageOptionGraphicResourceUrl"
          />
          <div v-if="showImageDropZones" class="preview-drop-grid">
            <div
              v-for="(zone, index) in imageDropZones"
              :key="`${zone.label}-${index}`"
              class="preview-drop-cell preview-drop-cell--image"
              :class="{ 'preview-drop-cell-active': previewDropZoneIndex === index }"
              :style="{
                '--preview-image-drop-color': zone.color,
                '--preview-image-drop-border': zone.borderColor,
              }"
            >
              {{ zone.label }}
            </div>
          </div>
          <div v-if="showButtonDropZones" class="preview-drop-grid preview-drop-grid--button">
            <div
              v-for="(zone, index) in buttonDropZones"
              :key="`${zone.label}-${index}`"
              class="preview-drop-cell preview-drop-cell--button"
              :class="{ 'preview-drop-cell-active': previewDropZoneIndex === index }"
              :style="{
                '--preview-button-drop-color': zone.color,
                '--preview-button-drop-border': zone.borderColor,
              }"
            >
              {{ zone.label }}
            </div>
          </div>
          <div v-if="showAudioDropZones" class="preview-drop-grid preview-drop-grid--audio">
            <div
              v-for="(zone, index) in audioDropZones"
              :key="`${zone.label}-${index}`"
              class="preview-drop-cell preview-drop-cell--audio"
              :class="{ 'preview-drop-cell-active': previewDropZoneIndex === index }"
              :style="{
                '--preview-audio-drop-color': zone.color,
                '--preview-audio-drop-border': zone.borderColor,
              }"
            >
              {{ zone.label }}
            </div>
          </div>
          <div v-if="showCanvasDropUnavailable" class="preview-drop-unavailable">
            <div class="preview-drop-unavailable-title">不可用</div>
            <div class="preview-drop-unavailable-text">
              {{ !editor.selectedMap ? '请先选择剧情' : '当前资源不能直接拖入创建事件' }}
            </div>
          </div>
        </PreviewSurface>
      </div>
    </div>
  </div>
</template>

<style scoped>
.preview-block {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 0 0 var(--pv-preview-basis, 68%);
  border-bottom: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
}

.preview-placeholder {
  flex: 1;
  min-height: 0;
  padding: 6px;
  display: flex;
  flex-direction: column;
  background: var(--theme-bg-panel);
}

.preview-viewport-tag {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-card);
  overflow: hidden;
  transform: translateZ(0);
  touch-action: none;
}

.preview-canvas-host {
  flex: 1;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: var(--theme-bg-secondary);
  touch-action: none;
}

.preview-canvas-host-dropping {
  border: 1px solid var(--theme-accent);
  cursor: pointer;
}

.preview-canvas-stage {
  position: relative;
  display: flex;
  flex-shrink: 0;
}

.preview-game-canvas {
  display: block;
  background: #000000;
}

:deep(.preview-oaf-background) {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000000;
  pointer-events: none;
  user-select: none;
}

.preview-test-frame {
  display: block;
  width: 100%;
  height: 100%;
  background: #000000;
  flex-shrink: 0;
  border: 0;
  touch-action: none;
}

.preview-mouse-rect-overlay {
  position: absolute;
  border: 2px solid var(--theme-accent);
  box-sizing: border-box;
  pointer-events: none;
}

.preview-drop-grid {
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  grid-template-rows: repeat(5, minmax(0, 1fr));
  gap: 2px;
  padding: 2px;
  pointer-events: none;
}

.preview-drop-grid--button {
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
}

.preview-drop-grid--audio {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  grid-template-rows: minmax(0, 1fr);
}

.preview-drop-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  padding: 4px;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-raised);
  color: var(--theme-text-secondary);
  font-size: 11px;
  line-height: 1.3;
  text-align: center;
  opacity: 0.8;
}

.preview-drop-cell-active {
  border-color: var(--theme-accent);
  background: var(--theme-bg-selected);
  color: var(--theme-text-heading);
  opacity: 1;
}

.preview-drop-cell--image {
  border-color: var(--preview-image-drop-border);
  background: var(--preview-image-drop-color);
  color: #ffffff;
}

.preview-drop-cell--image.preview-drop-cell-active {
  border-color: #ffffff;
  background: var(--preview-image-drop-color);
  color: #ffffff;
}

.preview-drop-cell--button {
  border-color: var(--preview-button-drop-border);
  background: var(--preview-button-drop-color);
  color: #ffffff;
}

.preview-drop-cell--button.preview-drop-cell-active {
  border-color: #ffffff;
  background: var(--preview-button-drop-color);
  color: #ffffff;
}

.preview-drop-cell--audio {
  border-color: var(--preview-audio-drop-border);
  background: var(--preview-audio-drop-color);
  color: #ffffff;
}

.preview-drop-cell--audio.preview-drop-cell-active {
  border-color: #ffffff;
  background: var(--preview-audio-drop-color);
  color: #ffffff;
}

.preview-drop-unavailable {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid #8b3a3a;
  background: rgba(64, 16, 16, 0.82);
  color: #ffd7d7;
  pointer-events: none;
}

.preview-drop-unavailable-title {
  font-size: 16px;
  font-weight: 700;
}

.preview-drop-unavailable-text {
  font-size: 12px;
}

</style>
